import { Home, Calendar, MessageCircle, StickyNote, Users, FolderOpen, Settings, User, Layout, LogOut, Menu, ChevronLeft, Palette, BookOpen, ChevronDown, ChevronRight, Columns, Plus, ListTodo, MoreHorizontal, Briefcase, Circle, UserPlus, Link2, Check, X, Mail, Copy } from 'lucide-react'
import { useUIStore } from '../../stores/ui-store'
import { useState, useEffect, useMemo } from 'react'
import { useAuthStore } from '../../stores/auth-store'
import { trpc } from '../../lib/trpc'
import KrushrLogo from '../common/KrushrLogo'
import { useNavigate } from 'react-router'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import QuickInvite from '../workspace/QuickInvite'
import QuickInviteCompact from '../workspace/QuickInviteCompact'
import { toast } from 'sonner'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import {
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface SidebarProps {
  currentPage: string
  onPageChange: (page: string) => void
}

interface NavigationItem {
  name: string
  icon: any
  href: string
  key: string
}

interface SortableNavItemProps {
  item: NavigationItem
  isActive: boolean
  isCollapsed: boolean
  onPageChange: (page: string) => void
}

function SortableNavItem({ item, isActive, isCollapsed, onPageChange }: SortableNavItemProps) {
  const navigate = useNavigate()
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.key })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative"
    >
      <button
        {...attributes}
        {...listeners}
        onClick={(e) => {
          e.preventDefault()
          navigate(item.href)
          onPageChange(item.key)
        }}
        className={`w-full flex items-center ${isCollapsed ? 'justify-center px-2' : 'px-3'} py-1.5 text-sm font-light rounded-lg transition-colors cursor-pointer ${
          isActive
            ? 'bg-krushr-primary text-white font-medium'
            : 'text-sidebar-foreground hover:text-sidebar-accent-foreground'
        } ${isDragging ? 'cursor-grabbing' : 'cursor-pointer'}`}
        title={isCollapsed ? item.name : undefined}
      >
        <item.icon className={`w-4 h-4 ${isCollapsed ? '' : 'mr-2'} ${isActive ? '' : 'text-sidebar-icon'}`} />
        {!isCollapsed && item.name}
      </button>
    </div>
  )
}

// Quick actions component for team members
function QuickActions({ member, navigate, onPageChange }: { 
  member: any; 
  navigate: (path: string) => void;
  onPageChange: (page: string) => void;
}) {
  const { openTaskCreationModal } = useUIStore();
  
  const handleAssignTask = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Open task creation modal with member pre-assigned
    openTaskCreationModal({
      assigneeId: member.id,
      assigneeName: member.name
    });
  };
  
  const handleViewTasks = (e: React.MouseEvent) => {
    e.stopPropagation();
    const currentHash = window.location.hash;
    const isCurrentlyShowingMemberTasks = currentHash.includes(`/board?assignee=${member.id}`);
    
    if (isCurrentlyShowingMemberTasks) {
      // Already showing tasks, toggle off by going back to workspace
      navigate('/workspace');
    } else {
      // Navigate to board with filter to show ALL tasks for this person
      navigate(`/board?assignee=${member.id}`);
    }
  };
  
  const handleMessage = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Open direct message chat with this specific member
    onPageChange('chat');
    // Store the specific member ID for their individual chat
    sessionStorage.setItem('openChatWith', member.id);
  };
  
  return (
    <div className="flex items-center space-x-0.5">
      <button 
        className="p-0.5 hover:bg-krushr-primary/20 rounded transition-all duration-200 hover:scale-110 transform active:scale-95"
        title="Assign task"
        onClick={handleAssignTask}
      >
        <Plus className="w-3 h-3 text-sidebar-foreground/70 hover:text-krushr-primary transition-colors duration-200" />
      </button>
      <button 
        className="p-0.5 hover:bg-krushr-primary/20 rounded transition-all duration-200 hover:scale-110 transform active:scale-95"
        title="View tasks"
        onClick={handleViewTasks}
      >
        <ListTodo className="w-3 h-3 text-sidebar-foreground/70 hover:text-krushr-primary transition-colors duration-200" />
      </button>
      <button 
        className="p-0.5 hover:bg-krushr-primary/20 rounded transition-all duration-200 hover:scale-110 transform active:scale-95"
        title="Message"
        onClick={handleMessage}
      >
        <MessageCircle className="w-3 h-3 text-sidebar-foreground/70 hover:text-krushr-primary transition-colors duration-200" />
      </button>
    </div>
  );
}

export default function Sidebar({ currentPage, onPageChange }: SidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [isBrandkitOpen, setIsBrandkitOpen] = useState(false)
  const [navigationOrder, setNavigationOrder] = useState<string[]>([])
  const [showInviteInput, setShowInviteInput] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteLinkCopied, setInviteLinkCopied] = useState(false)
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const { data: workspaces = [] } = trpc.workspace.list.useQuery()
  const activeWorkspace = workspaces[0]
  
  // Quick invite link generation for collapsed state
  const generateInviteLink = trpc.auth.generateInviteLink.useMutation({
    onSuccess: (data) => {
      navigator.clipboard.writeText(data.link)
      toast.success('Invite link copied!')
      setInviteLinkCopied(true)
      // Reset after 2 seconds
      setTimeout(() => setInviteLinkCopied(false), 2000)
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to generate invite link')
    }
  })
  
  // Get user preferences including navigation order
  const { data: preferences } = trpc.preferences.get.useQuery()
  const updateNavigationOrder = trpc.preferences.updateNavigationOrder.useMutation()
  
  // Set up drag and drop sensors with activation distance to prevent interference with clicking
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px of movement before dragging starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )
  
  const { data: workspaceMembers = [], isLoading: membersLoading, error: membersError } = trpc.user.listWorkspaceMembers.useQuery(
    { workspaceId: activeWorkspace?.id || '' },
    { 
      enabled: !!activeWorkspace?.id,
      staleTime: 0, // No cache - always fetch fresh data
      retry: 3,
      refetchOnWindowFocus: true
    }
  )
  
  // Get task counts for each member
  const { data: tasks = [] } = trpc.task.list.useQuery(
    { workspaceId: activeWorkspace?.id || '' },
    { 
      enabled: !!activeWorkspace?.id,
      staleTime: 30 * 1000, // Cache for 30 seconds
    }
  )
  
  // Get member task counts and current tasks
  const getMemberTaskInfo = (memberId: string) => {
    const memberTasks = tasks.filter(task => task.assigneeId === memberId)
    const activeTasks = memberTasks.filter(task => 
      task.status === 'IN_PROGRESS' || task.status === 'TODO'
    )
    const currentTask = memberTasks.find(task => task.status === 'IN_PROGRESS')
    
    return {
      totalCount: memberTasks.length,
      activeCount: activeTasks.length,
      currentTask: currentTask?.title || null,
      workloadPercentage: Math.min((activeTasks.length / 10) * 100, 100) // Max 10 tasks as 100%
    }
  }
  
  const displayMembers = workspaceMembers.length > 0 ? workspaceMembers : []
  const shouldShowMembersList = activeWorkspace && !membersError
  
  // Create navigation items with current workspace ID
  const defaultNavigation: NavigationItem[] = useMemo(() => {
    const dashboardHref = activeWorkspace?.id 
      ? `/dashboard/${activeWorkspace.id}` 
      : '/dashboard/cmec2dg7u0005otktl1bjv5zc'; // Default to alice's workspace
      
    return [
      { name: 'Dashboard', icon: Home, href: dashboardHref, key: 'dashboard' },
      { name: 'Workspace', icon: Layout, href: '/workspace', key: 'workspace' },
      { name: 'Kanban', icon: Columns, href: '/board', key: 'board' },
      { name: 'Calendar', icon: Calendar, href: '/calendar', key: 'calendar' },
      { name: 'Chat', icon: MessageCircle, href: '/chat', key: 'chat' },
      { name: 'Notes', icon: StickyNote, href: '/notes', key: 'notes' },
      { name: 'Teams', icon: Users, href: '/teams', key: 'teams' },
      { name: 'Projects', icon: FolderOpen, href: '/projects', key: 'projects' },
      { name: 'Contacts', icon: User, href: '/contacts', key: 'contacts' },
    ];
  }, [activeWorkspace])
  
  // Initialize navigation order from preferences
  useEffect(() => {
    if (preferences?.sidebarNavigationOrder) {
      try {
        let savedOrder = JSON.parse(preferences.sidebarNavigationOrder)
        
        // Migration: Replace 'home' with 'dashboard'
        savedOrder = savedOrder.map((key: string) => key === 'home' ? 'dashboard' : key)
        
        // Ensure dashboard is included if missing
        if (!savedOrder.includes('dashboard')) {
          savedOrder.unshift('dashboard')
        }
        
        setNavigationOrder(savedOrder)
      } catch {
        // If parsing fails, use default order
        setNavigationOrder(defaultNavigation.map(item => item.key))
      }
    } else {
      setNavigationOrder(defaultNavigation.map(item => item.key))
    }
  }, [preferences, defaultNavigation])
  
  // Get ordered navigation items
  const navigation = navigationOrder.length > 0 
    ? navigationOrder.map(key => defaultNavigation.find(item => item.key === key)!).filter(Boolean)
    : defaultNavigation

  const brandkitItems = [
    { name: 'Brandkit', icon: Palette, href: '/brandkit.html', key: 'brandkit' },
    { name: 'Brandkit 2', icon: Palette, href: '/brandkit-2.html', key: 'brandkit2' },
    { name: 'Landing Design System', icon: BookOpen, href: '/design-system-landing.html', key: 'landingkit' },
  ]

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = navigationOrder.indexOf(active.id as string)
      const newIndex = navigationOrder.indexOf(over.id as string)
      
      const newOrder = arrayMove(navigationOrder, oldIndex, newIndex)
      setNavigationOrder(newOrder)
      
      // Save to preferences
      updateNavigationOrder.mutate({ order: newOrder })
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <div className={`${isCollapsed ? 'w-16' : 'w-48'} flex flex-col h-full transition-all duration-300 sidebar-transparent relative`}>
      {/* Logo section with same background as header */}
      <div className="h-16 flex-shrink-0 krushr-header-base border-b-0 border-r-0">
        <div className="h-full flex items-center justify-between px-4">
          <div className="flex-1">
            <KrushrLogo size={isCollapsed ? "sm" : "md"} showText={!isCollapsed} />
          </div>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-1 rounded transition-all duration-200 ml-2 hover:bg-white/20 hover:scale-110 active:scale-95"
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? (
              <Menu className="w-4 h-4 text-sidebar-foreground" />
            ) : (
              <ChevronLeft className="w-4 h-4 text-sidebar-foreground" />
            )}
          </button>
        </div>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="px-4 py-3 flex-shrink-0">
          <nav className="space-y-0.5">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={navigationOrder}
                strategy={verticalListSortingStrategy}
              >
                {navigation.map((item) => {
                  const isActive = currentPage === item.key
                  return (
                    <SortableNavItem
                      key={item.key}
                      item={item}
                      isActive={isActive}
                      isCollapsed={isCollapsed}
                      onPageChange={onPageChange}
                    />
                  )
                })}
              </SortableContext>
            </DndContext>

          <div>
            <button
              onClick={() => setIsBrandkitOpen(!isBrandkitOpen)}
              className={`w-full flex items-center ${isCollapsed ? 'justify-center px-2' : 'px-3'} py-1.5 text-sm font-light rounded-lg transition-colors text-sidebar-foreground hover:text-sidebar-accent-foreground`}
              title={isCollapsed ? "Design System" : undefined}
            >
              <Palette className={`w-4 h-4 ${isCollapsed ? '' : 'mr-2'} text-sidebar-icon`} />
              {!isCollapsed && (
                <>
                  <span className="flex-1 text-left">Design System</span>
                  {isBrandkitOpen ? (
                    <ChevronDown className="w-4 h-4 text-sidebar-icon" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-sidebar-icon" />
                  )}
                </>
              )}
            </button>

            {isBrandkitOpen && !isCollapsed && (
              <div className="ml-6 mt-0.5 space-y-0.5">
                {brandkitItems.map((item) => (
                  <a
                    key={item.key}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center px-3 py-1.5 text-sm font-light rounded-lg transition-colors text-sidebar-foreground hover:text-sidebar-accent-foreground"
                  >
                    <item.icon className="w-4 h-4 mr-2 text-sidebar-icon" />
                    {item.name}
                  </a>
                ))}
              </div>
            )}
          </div>
        </nav>
        </div>

        {shouldShowMembersList && (
          <div className={`${isCollapsed ? 'px-2' : 'px-3'} flex-1 flex flex-col overflow-y-auto`}>
          {/* Team header */}
          {!isCollapsed && (
            <h3 className="text-xs font-semibold text-sidebar-foreground/70 uppercase tracking-wide mb-2 flex-shrink-0">
              Team {membersLoading ? '...' : `(${displayMembers.length})`}
            </h3>
          )}
          
          {/* Team members list */}
          <div className="flex flex-col">
          
          <div className={`space-y-1 ${isCollapsed ? 'flex flex-col items-center' : ''}`}>
            {membersLoading && displayMembers.length === 0 && (
              <div className={`flex ${isCollapsed ? 'justify-center' : 'items-center space-x-2'} p-1`}>
                <div className="w-7 h-7 rounded-full  animate-pulse"></div>
                {!isCollapsed && (
                  <div className="flex-1">
                    <div className="h-2.5  rounded animate-pulse mb-1"></div>
                    <div className="h-2 /60 rounded w-3/4 animate-pulse"></div>
                  </div>
                )}
              </div>
            )}
            
            {displayMembers.map((member, index) => {
              // Get real task data for member
              const taskInfo = getMemberTaskInfo(member.id);
              
              // Status rotation for demo (would come from presence API)
              const statuses = ['online', 'busy', 'away', 'offline'];
              const status = statuses[index % 4];
              
              const getStatusColor = (status: string) => {
                switch(status) {
                  case 'online': return 'bg-emerald-400';
                  case 'busy': return 'bg-blue-400';
                  case 'away': return 'bg-yellow-400';
                  case 'offline': return 'bg-gray-400';
                  default: return 'bg-gray-400';
                }
              };
              
              // Remove emoji - just use colored dots
              
              return (
              <div 
                key={member.id} 
                className={`group transition-all duration-200 cursor-pointer transform hover:scale-[1.02] ${
                  isCollapsed 
                    ? 'flex justify-center' 
                    : 'flex items-center space-x-2 p-1.5 rounded-lg hover:bg-white/10 hover:shadow-sm'
                }`}
                title={isCollapsed ? `${member.name}${taskInfo.currentTask ? ` - ${taskInfo.currentTask}` : ''} (${taskInfo.activeCount} active tasks)` : undefined}
                onClick={() => navigate(`/contacts?user=${member.id}`)}
              >
                <div className="relative flex-shrink-0">
                  <Avatar className="w-7 h-7 group-hover:scale-105 transition-all duration-200 group-hover:shadow-lg">
                    <AvatarImage src={member.avatar} alt={member.name} />
                    <AvatarFallback className="bg-gradient-to-br from-krushr-primary to-krushr-primary/80 text-white text-xs font-medium">
                      {getInitials(member.name || 'U')}
                    </AvatarFallback>
                  </Avatar>
                  {/* Status indicator with pulse animation for online users */}
                  <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 ${getStatusColor(status)} border-2 border-white rounded-full shadow-sm transition-all duration-200 ${
                    status === 'online' ? 'animate-pulse' : ''
                  }`} 
                       title={status} />
                </div>
                
                {!isCollapsed && (
                  <div className="flex-1 min-w-0">
                    {/* Name - single line with truncation */}
                    <p className="text-xs font-medium text-sidebar-foreground leading-tight group-hover:text-gray-900 transition-colors duration-200 truncate">
                      {member.name}
                    </p>
                    {/* Current task or status */}
                    <p className="text-[10px] text-sidebar-foreground/60 leading-tight group-hover:text-gray-700 transition-colors duration-200 truncate">
                      {taskInfo.currentTask || `${taskInfo.activeCount} active task${taskInfo.activeCount === 1 ? '' : 's'}`}
                    </p>
                    {/* Workload indicator with quick actions */}
                    {taskInfo.activeCount > 0 ? (
                      <div className="mt-0.5 flex items-center space-x-1">
                        <div className="flex-1 h-1 bg-krushr-primary/20 rounded-full overflow-hidden group-hover:bg-krushr-primary/30 transition-colors duration-200">
                          <div 
                            className="h-full bg-gradient-to-r from-krushr-primary to-krushr-secondary transition-all duration-500 ease-out"
                            style={{ width: `${taskInfo.workloadPercentage}%` }}
                          />
                        </div>
                        <span className="text-[9px] text-sidebar-foreground/50 group-hover:text-gray-600 transition-colors duration-200 mr-1">{taskInfo.totalCount}</span>
                        {/* Quick actions inline with progress */}
                        <div className="transform translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-300 delay-75">
                          <QuickActions 
                            member={member}
                            navigate={navigate}
                            onPageChange={onPageChange}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="mt-0.5 flex items-center justify-end">
                        {/* Quick actions when no progress bar */}
                        <div className="transform translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-300 delay-75">
                          <QuickActions 
                            member={member}
                            navigate={navigate}
                            onPageChange={onPageChange}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              );
            })}
            
            {/* Removed 'more members' indicator - show all members */}
            
            {!membersLoading && displayMembers.length === 0 && !isCollapsed && (
              <div className="text-xs text-sidebar-foreground/60 text-center py-1">
                No team members yet
              </div>
            )}
            
            {/* Invite section - inside the same space-y-1 container */}
            {!isCollapsed && (
              showInviteInput ? (
                <div className="group flex items-center space-x-2 p-1.5 rounded-lg hover:bg-white/10">
                  <div className="relative flex-shrink-0">
                    <Avatar className="w-7 h-7">
                      <AvatarFallback className="bg-black/30 text-white text-xs font-medium">
                        <Plus className="w-3 h-3" />
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  <div className="flex-1 min-w-0">
                    <input
                      type="email"
                      placeholder="Enter email..."
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && inviteEmail.trim()) {
                          e.preventDefault()
                          // TODO: Call invite mutation
                          toast.success(`Invite sent to ${inviteEmail}`)
                          setInviteEmail('')
                          setShowInviteInput(false)
                        }
                        if (e.key === 'Escape') {
                          setInviteEmail('')
                          setShowInviteInput(false)
                        }
                      }}
                      onBlur={() => {
                        if (!inviteEmail.trim()) {
                          setShowInviteInput(false)
                        }
                      }}
                      className="w-full bg-transparent text-xs font-medium text-sidebar-foreground placeholder:text-sidebar-foreground/40
                               focus:outline-none leading-tight"
                      autoFocus
                    />
                  </div>
                </div>
              ) : (
                <div 
                  className="group flex items-center space-x-2 p-1.5 rounded-lg hover:bg-white/10 cursor-pointer transition-all duration-200 transform hover:scale-[1.02] hover:shadow-sm"
                  onClick={() => setShowInviteInput(true)}
                >
                  <div className="relative flex-shrink-0">
                    <Avatar className="w-7 h-7 group-hover:scale-105 transition-all duration-200 group-hover:shadow-md">
                      <AvatarFallback className="bg-black/30 text-white text-xs font-medium group-hover:bg-krushr-primary transition-colors duration-200">
                        <Plus className="w-3 h-3 group-hover:rotate-90 transition-transform duration-300" />
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-sidebar-foreground/50 leading-tight group-hover:text-gray-800 transition-colors duration-200">Add member</p>
                    <p className="text-[10px] text-sidebar-foreground/40 leading-tight group-hover:text-gray-600 transition-colors duration-200">Click to invite</p>
                  </div>
                </div>
              )
            )}
          </div>
          
          
          {/* Collapsed state - single invite button */}
          {isCollapsed && (
            <div className="mt-3 flex justify-center">
              <button
                onClick={() => {
                  generateInviteLink.mutate({ 
                    workspaceId: activeWorkspace?.id || '',
                    role: 'MEMBER'
                  })
                }}
                className={`p-2 rounded-lg transition-all duration-200 transform hover:scale-110 active:scale-95 ${
                  inviteLinkCopied
                    ? 'bg-green-500/20 text-green-400 animate-pulse'
                    : 'hover:bg-white/10 text-sidebar-foreground/60 hover:text-sidebar-foreground hover:shadow-md'
                }`}
                title={inviteLinkCopied ? 'Link copied!' : 'Copy invite link'}
              >
                {inviteLinkCopied ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <UserPlus className="w-4 h-4" />
                )}
              </button>
            </div>
          )}
          </div>{/* End team list container */}
          
          {/* Spacer to push content up when list is short */}
          <div className="flex-1"></div>
        </div>
        )}

        {!shouldShowMembersList && (
          <div className="flex-1"></div>
        )}
      </div>

      {user && (
        <div className="p-3 flex-shrink-0">
          {!isCollapsed ? (
            <div className="flex items-center space-x-2 p-1.5 rounded-lg  transition-colors">
              <Avatar className="w-7 h-7">
                <AvatarImage src={user.avatar} />
                <AvatarFallback className="bg-gradient-to-br from-pink-400 to-red-500 text-white text-xs font-medium">
                  {getInitials(user.name || 'U')}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-sidebar-foreground truncate leading-tight">{user.name}</p>
                <p className="text-xs text-sidebar-foreground/60 truncate leading-tight">{user.email}</p>
              </div>
              <div className="flex space-x-0.5">
                <button
                  onClick={() => navigate('/settings')}
                  className="p-1  rounded transition-colors"
                  title="Settings"
                >
                  <Settings className="w-3.5 h-3.5 text-sidebar-foreground/60" />
                </button>
                <button
                  onClick={handleLogout}
                  className="p-1  hover:text-red-600 rounded transition-colors"
                  title="Logout"
                >
                  <LogOut className="w-3.5 h-3.5 text-sidebar-foreground/60" />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center space-y-1">
              <Avatar className="w-7 h-7" title={user.name}>
                <AvatarImage src={user.avatar} />
                <AvatarFallback className="bg-gradient-to-br from-pink-400 to-red-500 text-white text-xs font-medium">
                  {getInitials(user.name || 'U')}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col space-y-0.5">
                <button
                  onClick={() => navigate('/settings')}
                  className="p-1  rounded transition-colors"
                  title="Settings"
                >
                  <Settings className="w-3.5 h-3.5 text-sidebar-foreground/60" />
                </button>
                <button
                  onClick={handleLogout}
                  className="p-1  hover:text-red-600 rounded transition-colors"
                  title="Logout"
                >
                  <LogOut className="w-3.5 h-3.5 text-sidebar-foreground/60" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
